
import { apiClient } from '@/lib/apiClient';

export interface ElevenLabsAgent {
  agent_id: string;
  name: string;
  gender: string;
  age_group: string;
  accent: string;
  description: string;
  is_default: boolean;
  preview_url: string;
  tags?: string[];
  metadata?: {
    created_at_unix_secs: number;
  };
  access_info?: {
    is_creator: boolean;
    creator_name: string;
    creator_email: string;
    role: string;
  };
}

// Simple in-memory cache for agents
let cachedAgents: ElevenLabsAgent[] | null = null;
let lastFetchTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

class ElevenLabsAgentService {
  private getApiKey(): string | null {
    // Try to get API key from environment or localStorage
    return import.meta.env.VITE_ELEVENLABS_API_KEY || 
           localStorage.getItem('elevenlabs_api_key') || 
           null;
  }

  private isConfigured(): boolean {
    return !!this.getApiKey();
  }

  private getHeaders() {
    const apiKey = this.getApiKey();
    if (!apiKey) {
      throw new Error('ElevenLabs API key not configured');
    }
    
    return {
      'xi-api-key': apiKey,
      'Content-Type': 'application/json'
    };
  }

  async getAllAgents(): Promise<ElevenLabsAgent[]> {
    if (!this.isConfigured()) {
      console.log('ElevenLabs API key not configured');
      return [];
    }

    // Check cache first
    const now = Date.now();
    if (cachedAgents && (now - lastFetchTime) < CACHE_DURATION) {
      return cachedAgents;
    }

    try {
      const response = await fetch('https://api.elevenlabs.io/v1/convai/agents', {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`ElevenLabs API error: ${response.status}`);
      }

      const data = await response.json();
      cachedAgents = data.agents || [];
      lastFetchTime = now;
      return cachedAgents;
    } catch (error) {
      console.warn('Failed to fetch ElevenLabs agents:', error);
      return [];
    }
  }

  async getAgentById(agentId: string): Promise<ElevenLabsAgent | null> {
    if (!this.isConfigured()) {
      return null;
    }

    try {
      const response = await fetch(`https://api.elevenlabs.io/v1/convai/agents/${agentId}`, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`ElevenLabs API error: ${response.status}`);
      }

      const data = await response.json();
      return data as ElevenLabsAgent;
    } catch (error) {
      console.warn(`Failed to fetch ElevenLabs agent ${agentId}:`, error);
      return null;
    }
  }

  async getDefaultAgent(): Promise<ElevenLabsAgent | null> {
    if (!this.isConfigured()) {
      return null;
    }

    try {
      // Get all agents and find the default one
      const agents = await this.getAllAgents();
      return agents.find(agent => agent.is_default) || null;
    } catch (error) {
      console.warn('Failed to fetch default ElevenLabs agent:', error);
      return null;
    }
  }

  async updateAgent(agentId: string, data: any): Promise<ElevenLabsAgent | null> {
    if (!this.isConfigured()) {
      return null;
    }

    try {
      const response = await fetch(`https://api.elevenlabs.io/v1/convai/agents/${agentId}`, {
        method: 'PATCH',
        headers: this.getHeaders(),
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`ElevenLabs API error: ${response.status}`);
      }

      // Clear cache since we updated an agent
      cachedAgents = null;
      
      const updatedAgent = await response.json();
      return updatedAgent as ElevenLabsAgent;
    } catch (error) {
      console.error('Failed to update ElevenLabs agent:', error);
      throw error;
    }
  }

  async createAgent(data: any): Promise<ElevenLabsAgent | null> {
    if (!this.isConfigured()) {
      return null;
    }

    try {
      const response = await fetch('https://api.elevenlabs.io/v1/convai/agents', {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`ElevenLabs API error: ${response.status}`);
      }

      // Clear cache since we created a new agent
      cachedAgents = null;
      
      const newAgent = await response.json();
      return newAgent as ElevenLabsAgent;
    } catch (error) {
      console.error('Failed to create ElevenLabs agent:', error);
      throw error;
    }
  }
}

export const elevenLabsAgentService = new ElevenLabsAgentService();

// Export functions with proper conversions
export const getElevenLabsAgents = () => elevenLabsAgentService.getAllAgents();
export const getElevenLabsAgentById = (id: string) => elevenLabsAgentService.getAgentById(id.replace('el_', ''));
export const updateElevenLabsAgent = async (id: string, data: any) => {
  return await elevenLabsAgentService.updateAgent(id.replace('el_', ''), data);
};
export const createElevenLabsAgent = async (data: any) => {
  return await elevenLabsAgentService.createAgent(data);
};
export const convertElevenLabsToAgentConfig = (agent: ElevenLabsAgent) => {
  return {
    id: `el_${agent.agent_id}`,
    name: agent.name,
    role: 'AI Assistant',
    goal: 'conversation',
    active: true
  };
};
export const convertAgentConfigToElevenLabs = (config: any) => {
  return {
    name: config.name,
    conversation_config: {
      agent: {
        prompt: {
          prompt: config.persona || config.system_prompt || '',
          temperature: parseFloat(config.llm?.temperature || '0.7'),
        },
        first_message: config.first_message || 'Hello! How can I help you today?',
        language: config.language || 'en'
      }
    }
  };
};
export const convertAgentConfigToElevenLabsCreate = (config: any) => {
  return {
    name: config.name,
    conversation_config: {
      agent: {
        prompt: {
          prompt: config.persona || config.system_prompt || '',
          temperature: parseFloat(config.llm?.temperature || '0.7'),
        },
        first_message: config.first_message || 'Hello! How can I help you today?',
        language: config.language || 'en'
      }
    }
  };
};
